import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FileText, BarChart3, Play, CalendarCheck, Unlock, Trophy } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { scrollToId } from "../lib/scroll";
import { PHOTOS } from "../data/siteData";
import { PhotoFrame } from "./PhotoFrame";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { lang, t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative overflow-hidden pt-16"
      data-testid="hero-section"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 10%, rgba(227,48,42,0.16) 0%, transparent 70%)",
        }}
      />
      <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-14 lg:pb-24 lg:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-3 border border-ink-600/70 bg-ink-900/70 px-4 py-2.5"
            data-testid="hero-ikons-badge"
          >
            <img
              src="/images/ikons-logo.png"
              alt="IKONS by Solocracks"
              className="h-6 w-auto"
              data-testid="hero-ikons-logo"
            />
            <span className="h-4 w-px bg-ink-600" aria-hidden="true" />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-kit">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1
            data-testid="hero-name-heading"
            className="mt-5 font-display text-[clamp(3.5rem,12vw,8.5rem)] font-extrabold uppercase leading-[0.88] tracking-tight text-white"
          >
            <MaskLine delay={0.2}>
              <span className="text-kit">{t.hero.name1}</span>
            </MaskLine>
            <MaskLine delay={0.32}>{t.hero.name2}</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            data-testid="hero-subtitle"
            className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300 sm:text-xs"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.68 }}
            className="mt-4 max-w-md text-base text-zinc-400 sm:text-lg"
          >
            {t.hero.quote}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <button
              data-testid="hero-cta-profile"
              onClick={() => scrollToId("perfil")}
              className="inline-flex items-center gap-2 bg-kit px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-ink-950 transition-colors duration-200 hover:bg-kit-hover"
            >
              <FileText size={16} /> {t.hero.ctaProfile}
            </button>
            <button
              data-testid="hero-cta-stats"
              onClick={() => scrollToId("estadisticas")}
              className="inline-flex items-center gap-2 border border-ink-600 px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:border-kit hover:text-kit"
            >
              <BarChart3 size={16} /> {t.hero.ctaStats}
            </button>
            <button
              data-testid="hero-cta-videos"
              onClick={() => scrollToId("videos")}
              className="inline-flex items-center gap-2 border border-ink-600 px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:border-kit hover:text-kit"
            >
              <Play size={16} /> {t.hero.ctaVideos}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.92 }}
            className="mt-8 flex flex-col gap-2"
          >
            <span
              data-testid="contract-status-badge"
              className="inline-flex w-fit items-center gap-2 border border-kit/40 bg-kit/10 px-3 py-1.5 text-xs font-semibold text-kit"
            >
              <CalendarCheck size={14} /> {t.hero.contract}
            </span>
            <span
              data-testid="free-agent-status-badge"
              className="inline-flex w-fit items-center gap-2 border border-ink-600 px-3 py-1.5 text-xs font-semibold text-zinc-300"
            >
              <Unlock size={14} /> {t.hero.freeAgent}
            </span>
            <button
              data-testid="hero-ranking-badge"
              onClick={() => scrollToId("analisis")}
              className="inline-flex w-fit items-center gap-2 border border-amber-300/40 bg-amber-300/10 px-3 py-1.5 text-xs font-semibold text-amber-300 transition-colors duration-200 hover:bg-amber-300/20"
            >
              <Trophy size={14} /> {t.hero.rankingBadge}
            </button>
          </motion.div>
        </div>

        <motion.div
          style={{ y: yPhoto }}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <PhotoFrame
            src={PHOTOS.hero.src}
            alt={PHOTOS.hero.alt[lang]}
            label="C. DÍAZ · CDA"
            testId="hero-photo-frame"
            eager
            className="aspect-[3/4] w-full sm:aspect-[4/5]"
          />
        </motion.div>
      </div>
    </section>
  );
};
